import { safeRead, safeWrite } from '../../infra/storage';
/**
 * O que o LEITOR escolheu numa tabela: a ordem das colunas, as que escondeu e a
 * largura que deu a cada uma.
 *
 * Preferencia de quem le, e nao estado da tela: um link mandado a um colega abre nas
 * colunas DELE. Por isso no `localStorage`, com a tabela e o id da conta na chave, e
 * nunca na URL.
 *
 * Juntou o que eram duas guardas (19/10/2026): `CollaboratorColumns` (ordem e
 * escondidas, B-42) e `ColumnWidths` (largura). A escolha que ja estava gravada nas
 * chaves antigas e lida na primeira vez - ver `legacy`.
 */
const MEMORY = 'mormaii.table';
/** Os limites de uma coluna: estreita demais some, larga demais empurra as outras para fora. */
export const COLUMN_WIDTH = { min: 56, max: 720 };
export class TablePreferences {
    constructor(table, account, legacy = {}) {
        this.table = table;
        this.account = account;
        this.legacy = legacy;
        this.order = [];
        this.hidden = new Set();
        this.widths = new Map();
    }
    /**
     * Os campos na ordem escolhida. Campo que a ordem guardada nao conhece (coluna nova)
     * vai para o FIM, na ordem padrao: e o lugar que nao desloca escolha nenhuma.
     */
    arrange(fields) {
        this.ensureLoaded();
        const position = new Map(this.order.map((field, index) => [field, index]));
        const known = fields.filter((field) => position.has(field));
        const unknown = fields.filter((field) => !position.has(field));
        known.sort((left, right) => position.get(left) - position.get(right));
        return [...known, ...unknown];
    }
    isHidden(field) {
        this.ensureLoaded();
        return this.hidden.has(field);
    }
    toggle(field) {
        this.ensureLoaded();
        if (this.hidden.has(field)) {
            this.hidden.delete(field);
        }
        else {
            this.hidden.add(field);
        }
        this.save();
    }
    /**
     * Poe `source` no lugar de `target`, dentro de `fields` (as colunas que se movem).
     * Quem vem da esquerda cai depois do alvo, quem vem da direita cai antes.
     */
    move(fields, source, target) {
        if (source === target) {
            return false;
        }
        const current = this.arrange(fields);
        const from = current.indexOf(source);
        const to = current.indexOf(target);
        if (from < 0 || to < 0) {
            return false;
        }
        current.splice(from, 1);
        current.splice(to, 0, source);
        this.order = current;
        this.save();
        return true;
    }
    /** A largura escolhida, ou nada - e entao vale a que o conteudo pede. */
    widthOf(field) {
        this.ensureLoaded();
        return this.widths.get(field);
    }
    /** Guarda a largura nova, dentro dos limites, e devolve a que ficou. */
    resize(field, width) {
        this.ensureLoaded();
        const kept = clampWidth(width);
        this.widths.set(field, kept);
        this.save();
        return kept;
    }
    save() {
        const account = this.account();
        if (account === undefined) {
            return;
        }
        const saved = { order: this.order, hidden: [...this.hidden], widths: Object.fromEntries(this.widths) };
        safeWrite(`${MEMORY}:${this.table}:${account}`, JSON.stringify(saved));
    }
    /** Le a escolha da conta da vez - uma vez por conta. */
    ensureLoaded() {
        const account = this.account() ?? null;
        if (this.loadedFor === account) {
            return;
        }
        this.loadedFor = account;
        const saved = account === null ? undefined : this.read(account);
        this.order = saved?.order ?? [];
        this.hidden = new Set(saved?.hidden ?? []);
        this.widths = new Map(Object.entries(saved?.widths ?? {}));
        // Veio das chaves antigas: ja vai para a nova, sem esperar o leitor mexer em
        // alguma coisa. Sem isto, quem so CONSULTA a tabela perderia a escolha no dia em
        // que a leitura das chaves antigas sair.
        if (saved?.legacy) {
            this.save();
        }
    }
    read(account) {
        const current = parse(safeRead(`${MEMORY}:${this.table}:${account}`));
        if (current) {
            return current;
        }
        const columns = this.legacy.columns ? parse(safeRead(`${this.legacy.columns}:${account}`)) : undefined;
        const widths = this.legacy.widths ? parseWidths(safeRead(`${this.legacy.widths}:${account}`)) : undefined;
        if (!columns && !widths) {
            return undefined;
        }
        return { order: columns?.order, hidden: columns?.hidden, widths, legacy: true };
    }
}
export function clampWidth(width) {
    return Math.round(Math.min(COLUMN_WIDTH.max, Math.max(COLUMN_WIDTH.min, width)));
}
/** O que foi gravado, ou nada - dado velho ou mexido a mao. Parte fora da forma fica de fora. */
function parse(raw) {
    const value = json(raw);
    // So a ordem, como o antigo `HistoryColumnOrder` gravava.
    if (Array.isArray(value)) {
        return value.every((item) => typeof item === 'string') ? { order: value } : undefined;
    }
    if (!isRecord(value)) {
        return undefined;
    }
    const strings = (list) => Array.isArray(list) && list.every((item) => typeof item === 'string') ? list : undefined;
    return {
        order: strings(value.order),
        hidden: strings(value.hidden),
        widths: isRecord(value.widths) ? widthsOf(value.widths) : undefined
    };
}
function parseWidths(raw) {
    const value = json(raw);
    return isRecord(value) ? widthsOf(value) : undefined;
}
function widthsOf(value) {
    const widths = {};
    for (const [field, width] of Object.entries(value)) {
        if (typeof width === 'number' && Number.isFinite(width)) {
            widths[field] = clampWidth(width);
        }
    }
    return widths;
}
function json(raw) {
    if (!raw) {
        return undefined;
    }
    try {
        return JSON.parse(raw);
    }
    catch {
        return undefined;
    }
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
//# sourceMappingURL=table-preferences.js.map